import React, { FC, Suspense, lazy, useState } from "react";
import { Box, Dialog, Typography } from "@mui/material";
import { BriefAttachment } from "./constants";
import { IconClose, IconDownload } from "../../utils/icons";
import {
    badgeLabelOf,
    isFramable,
    isPreviewableImage,
    isWordDocx,
    kindLabelOf,
} from "./attachmentKinds";
import { downloadAttachment } from "./downloadAttachment";

const DocxPreview = lazy(() =>
    import("./DocxPreview").then((m) => ({ default: m.DocxPreview })),
);

export interface PreviewTarget {
    name: string;
    url?: string;
}

interface Props {
    attachment: PreviewTarget | BriefAttachment | null;
    onClose: () => void;
}

const BADGE_CLASSES =
    "rounded-lg border border-[#EAD9B8] bg-tint flex items-center justify-center font-bold tracking-wide text-accent-text";

const Loading = () => (
    <Typography className="!text-[13px] text-muted py-10">Opening document…</Typography>
);

export const BriefAttachmentPreview: FC<Props> = ({ attachment, onClose }) => {
    const name = attachment?.name || "";
    const url =
        (attachment as BriefAttachment)?.location ||
        (attachment as PreviewTarget)?.url ||
        undefined;
    const [saving, setSaving] = useState(false);

    const showsImage = isPreviewableImage(name);
    const showsFrame = isFramable(name);
    const showsDocx = isWordDocx(name);

    const handleDownload = async () => {
        if (!url) return;
        setSaving(true);
        try {
            await downloadAttachment(url, name);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={!!attachment}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    maxWidth: "760px",
                    borderRadius: "18px",
                    overflow: "hidden",
                    backgroundColor: "var(--surface)",
                },
            }}
        >
            <Box className="flex items-center gap-3 px-4 py-3 border-b border-border-token bg-surface">
                <span className={`shrink-0 w-9 h-9 text-[9px] ${BADGE_CLASSES}`}>
                    {badgeLabelOf(name)}
                </span>
                <Box className="flex-1 min-w-0">
                    <Typography
                        className="!font-circular !text-[14px] !font-bold !leading-tight truncate text-text-1"
                        title={name}
                    >
                        {name}
                    </Typography>
                    <Typography className="!text-[11.5px] text-muted truncate">
                        {kindLabelOf(name)}
                    </Typography>
                </Box>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close preview"
                    title="Close"
                    className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-lg text-text-2 hover:bg-surface-2 hover:text-accent-text transition-colors"
                >
                    <IconClose size={15} stroke="currentColor" />
                </button>
            </Box>

            <Box className="p-5 bg-surface-2 flex items-center justify-center min-h-[280px]">
                {showsImage && url && (
                    <img
                        src={url}
                        alt={name}
                        className="max-w-full max-h-[70vh] object-contain rounded-xl shadow-[0_8px_28px_rgba(0,0,0,0.14)]"
                    />
                )}
                {showsFrame && url && (
                    <iframe
                        src={url}
                        title={name}
                        className="w-full h-[70vh] rounded-xl border-0 bg-surface shadow-[0_8px_28px_rgba(0,0,0,0.14)]"
                    />
                )}
                {showsDocx && url && (
                    <Suspense fallback={<Loading />}>
                        <DocxPreview url={url} />
                    </Suspense>
                )}
                {!showsImage && !showsFrame && !showsDocx && (
                    <Box className="flex flex-col items-center gap-3 py-10 text-center">
                        <span className={`w-14 h-14 text-[12px] ${BADGE_CLASSES}`}>
                            {badgeLabelOf(name)}
                        </span>
                        <Typography className="!text-[13px] text-text-2">
                            Legacy .doc files can&apos;t be opened in the browser. Download
                            the file, or re-save it as .docx to preview it here.
                        </Typography>
                        <button
                            type="button"
                            onClick={handleDownload}
                            disabled={saving || !url}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] font-semibold text-accent-text bg-tint border border-[#EAD9B8] hover:bg-[#F7EDD9] transition-colors disabled:opacity-60"
                        >
                            <IconDownload width={14} height={14} stroke="var(--accent-text)" />
                            {saving ? "Downloading…" : "Download file"}
                        </button>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};
